import { useEffect, useRef } from 'react';
import { sameStyleValue, whenStyleReady } from './utils';

// "Never applied", as distinct from a legitimately absent paint/filter.
const UNSET = Symbol('unset');

function Layer({ map, id, type = 'fill', paint, filter, ...props }) {
    // The deferred `add` must read current props, not what its render closed over -- the sync
    // effects below can't correct it later, since they skip while `getLayer` is undefined.
    const latest = useRef({ paint, filter, props });
    // What mapbox has actually been told, so an unchanged value can skip the setter.
    const applied = useRef({ paint: UNSET, filter: UNSET });

    // In an effect, not during render: an abandoned render must not leave a stale value behind.
    useEffect(() => {
        latest.current = { paint, filter, props };
    });

    useEffect(() => {
        if (!map) return undefined;

        const add = () => {
            if (map.getLayer(id)) return;

            const { paint: currentPaint, filter: currentFilter, props: rest } = latest.current;
            map.addLayer({
                ...rest,
                id,
                type,
                paint: currentPaint,
                ...(currentFilter ? { filter: currentFilter } : {}),
            });
            applied.current = { paint: currentPaint, filter: currentFilter };
        };

        const unsubscribe = whenStyleReady(map, add);

        return () => {
            unsubscribe();

            // Otherwise the layer outlives its component -- a deleted subregion kept its fill
            // painted forever. `getLayer` throws once the style is gone, when there is nothing
            // left to remove anyway.
            if (map.style && map.getLayer(id)) {
                map.removeLayer(id);
            }

            applied.current = { paint: UNSET, filter: UNSET };
        };
    }, [map, id, type]);

    useEffect(() => {
        if (!map || !map.getLayer(id)) return;
        if (sameStyleValue(applied.current.filter, filter)) return;

        applied.current.filter = filter;
        map.setFilter(id, filter);
    }, [map, id, filter]);

    useEffect(() => {
        if (!map || !map.getLayer(id)) return;
        if (sameStyleValue(applied.current.paint, paint)) return;

        const previous = applied.current.paint;
        applied.current.paint = paint;

        // The union of both key sets, not just the new one's. A key present in `previous` but
        // absent from `paint` has to be cleared explicitly -- mapbox keeps the last value
        // otherwise, and `applied.current.paint` would then claim the new object is what is
        // applied, so it never self-corrects on a later render either.
        const keys = new Set([...(previous === UNSET ? [] : Object.keys(previous || {})), ...Object.keys(paint)]);

        keys.forEach((key) => {
            const value = key in paint ? paint[key] : undefined;
            if (previous !== UNSET && sameStyleValue(previous?.[key], value)) return;
            // undefined resets the property to the style default, which is what a dropped key means.
            map.setPaintProperty(id, key, value);
        });
    }, [map, paint, id]);

    return null;
}

export default Layer;
